import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router';
import { getUserEntity } from 'common/UserEntityProvider';
import InvoicesSearchBar from './InvoicesSearchBar';
import InvoicesStatusSelect from './InvoicesStatusSelect';
import InvoicesAmountRange from './InvoicesAmountRange';
import DateRangePickerDialog from 'components/Dialog/DateRangePickerDialog';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import SearchButton from 'components/common/SearchButton';
import AddIcon from "@mui/icons-material/Add";
import { ENTITY_TYPE } from 'common/constants';
import intl from 'components/i18n/ReactIntlWrapper';

const InvoicesNavbar = ({ onSearch, invoiceStatus, allInvoices, showPlusButton = false, queryString, setQueryString }) => {
    const navigate = useNavigate();
    const searchBarRef = useRef(null);
    const [amounts, setAmounts] = useState({
        "min": "",
        "max": ""
    })
    const [idStatus, setIdStatus] = useState(null);
    const [range, setRange] = useState(null);
    const location = useLocation();

    const handleSearchButton = useCallback(() => {
        const currentQueryString = searchBarRef.current?.getValue() || '';
        setQueryString(currentQueryString);
        onSearch(currentQueryString, idStatus, range, amounts);
    }, [idStatus, range, amounts, onSearch, setQueryString]);

    const handleSearchButtonValidating = useCallback(() => {
        const currentQueryString = searchBarRef.current?.getValue() || '';
        setQueryString(currentQueryString);
        onSearch(currentQueryString, idStatus);
    }, [idStatus, onSearch, setQueryString]);

    // Memoize the onEnterPress handler to prevent creating new references
    const handleEnterPress = useCallback(() => {
        const currentQueryString = searchBarRef.current?.getValue() || '';
        setQueryString(currentQueryString);
        if (allInvoices) {
            onSearch(currentQueryString, idStatus, range, amounts);
        } else {
            onSearch(currentQueryString, idStatus);
        }
    }, [allInvoices, idStatus, range, amounts, onSearch, setQueryString]);

    const handleStatusChange = useCallback((e) => {
        if (e !== null) {
            setIdStatus(e.target.value);
        } else {
            setIdStatus(null);
        }
    }, []);

    useEffect(() => {
        setRange(null);
        setIdStatus(null);
        setAmounts({
            "min": "",
            "max": ""
        })
      }, [location.pathname]);

      const entityType = getUserEntity().entityType;

    return (
        <Grid
          container
          spacing={2} sx={{ mb: 2 }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid sx={{xs:12, md:(allInvoices ? 2 : 10)}}>
            <InvoicesSearchBar
              ref={searchBarRef}
              onEnterPress={handleEnterPress}
              queryString={queryString}
            />
          </Grid>

          {allInvoices && (
            <Grid sx={{xs:12, md:3.5}}>
              <DateRangePickerDialog
                disabled={false}
                inputName="dateRange"
                className="timesheet-datepicker"
                selected={range}
                setRange={setRange}
                required
                gap={2}
              />
            </Grid>
          )}

          {allInvoices && (
            <Grid sx={{xs:12, md:2}}>
              <InvoicesStatusSelect
                invoiceStatus={invoiceStatus}
                selectedStatus={idStatus}
                handleStatusChange={handleStatusChange}
              />
            </Grid>
          )}

          {allInvoices && (
            <Grid sx={{xs:12, md:2.5}}>
              <InvoicesAmountRange
                amounts={amounts}
                setAmounts={setAmounts}
                gap={2}
              />
            </Grid>
          )}

          <Grid sx={{xs:12, md:1}}>
            <SearchButton
              clickEvent={allInvoices ? handleSearchButton : handleSearchButtonValidating}
            />
          </Grid>
      
          {showPlusButton && (
            <Grid sx={{display: 'flex', justifyContent: 'flex-end', xs:12, md:"auto"}}>
              <Button
                onClick={() =>
                  entityType === ENTITY_TYPE.CORPORATE_ENTITY
                    ? navigate(`/invoicing/newinvoice`)
                    : navigate(`/myInvoices/upload`)
                }
                sx={{ minWidth: 40, height: 40 }}
              >
                <AddIcon /> {intl.get('invoice.new') ? intl.get('invoice.new') : intl.get('button.new')}
              </Button>
            </Grid>
          )}
        </Grid>);
}

export default React.memo(InvoicesNavbar);
